import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useSubmitTicketMutation } from "@/features/api/supportApi";

import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

import { toast } from "sonner";

const SupportTicketForm = () => {
  const { user, isAuthenticated } = useSelector(
    (state) => state.auth || {}
  );

  const [submitTicket, { isLoading }] =
    useSubmitTicketMutation();

  /* ================= PHONE ================= */
  const userPhone = useMemo(
    () => user?.phone || user?.mobile || "",
    [user]
  );

  const [formData, setFormData] = useState({
    subject: "",
    message: "",
    phone: "",
  });

  /* ================= PREFILL PHONE ================= */
  useEffect(() => {
    if (userPhone) {
      setFormData((prev) => ({
        ...prev,
        phone: userPhone,
      }));
    }
  }, [userPhone]);

  /* ================= CHANGE ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated || !user) {
      toast.error("Please log in to submit a ticket");
      return;
    }

    if (!formData.subject || !formData.message.trim()) {
      toast.error("Subject and message are required");
      return;
    }

    try {
      await submitTicket({
        name: String(user.name || ""),
        email: String(user.email || ""),
        subject: formData.subject,
        message: formData.message.trim(),
        phone: formData.phone || "N/A",
      }).unwrap();

      toast.success("Support ticket submitted successfully");

      setFormData({
        subject: "",
        message: "",
        phone: userPhone || "",
      });
    } catch (err) {
      toast.error(
        err?.data?.message || "Failed to submit ticket"
      );
    }
  };

  /* ================= UI ================= */
  return (
    <Card className="max-w-md mx-auto p-6">
      <h2 className="text-xl font-semibold text-center">
        Contact Support
      </h2>
      <p className="text-sm text-gray-500 text-center mb-6">
        Our team will get back to you shortly
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label>Name</Label>
          <Input value={user?.name || ""} disabled />
        </div>

        <div>
          <Label>Email</Label>
          <Input value={user?.email || ""} disabled />
        </div>

        <div>
          <Label>Phone (optional)</Label>
          <Input
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+91 9876543210"
          />
        </div>

        <div>
          <Label>Subject</Label>
          <select
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            className="w-full border rounded-md p-2 bg-background"
            required
          >
            <option value="">Select a subject</option>
            <option value="course">Course Related</option>
            <option value="work_permit">Work Permit</option>
            <option value="study_abroad">Study Abroad</option>
            <option value="website">Website Issue</option>
          </select>
        </div>

        <div>
          <Label>Message</Label>
          <Textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            required
          />
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={isLoading}
        >
          {isLoading ? "Submitting..." : "Submit Ticket"}
        </Button>
      </form>
    </Card>
  );
};

export default SupportTicketForm;
